import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { loadPageData } from "redux/actions/Request";
import moment from "moment-timezone";
import {
  useQueryControls,
  useResultControls,
} from "views/Components/QueryControls";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import CheckIcon from "@mui/icons-material/Check";
import { Alert, Skeleton } from "@mui/material";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";

import { setPageHashParams } from "utils";
import { abbreviateNumber } from "./helpers";
import { entityFields, entityTypes } from "./defs";

import { LegacyOverview } from "./Overview";

import debounce from "lodash/debounce";

import createOverviewEndpointQuery from "./query/overview";
import createTimelineQuery from "./query/timeline";

import SSLine from "./charts/timeline";

const normalize = (val, max, min) => (val - min) / (max - min);

/**
 * field name:
 * - op: API operator to use for matching
 * - validate: if search string does not pass validate function, it will not be used in request
 */

export const getQueryFieldsByEntityName = (entityName) => {
  const retVal = [];
  if (!Array.isArray(entityName)) {
    entityName = [entityName];
  }
  const eTypeList = entityTypes.filter((eT) => entityName.includes(eT.name));
  eTypeList.forEach((eT) => {
    eT.queryFields.forEach((f) => {
      if (!retVal.includes(f)) {
        retVal.push(f);
      }
    });
  });
  return retVal;
};

export const defaultStartTime =
  new moment().startOf("day").subtract(7, "days").unix() * 1000;
export const defaultEndTime = new moment().unix() * 1000;

const SimpleSearchInputPage = (props) => {
  const [searchValue, setSearchValue] = useState("");

  const handleChangeSearchValue = (event) => {
    setSearchValue(event.target.value);
  };

  const handleSubmit = () => {
    window.location.hash = `#search=${searchValue}`;
  };

  return (
    <>
      <TextField
        labelId="search-value-label"
        text={searchValue}
        onChange={handleChangeSearchValue}
        id="search-value-input"
      />
      <Button variant="contained" onClick={handleSubmit} color="primary">
        Investigate
      </Button>
      <Typography variant="h6">What can I start looking for?</Typography>
      <List component="nav" aria-label="main mailbox folders">
        <ListItem dense>
          <ListItemIcon>
            <CheckIcon />
          </ListItemIcon>
          <ListItemText primary="Users (usernames or email addresses)" />
        </ListItem>
        <ListItem dense>
          <ListItemIcon>
            <CheckIcon />
          </ListItemIcon>
          <ListItemText primary="IP Addresses" />
        </ListItem>
        <ListItem dense>
          <ListItemIcon>
            <CheckIcon />
          </ListItemIcon>
          <ListItemText primary="Ports" />
        </ListItem>
        <ListItem dense>
          <ListItemIcon>
            <CheckIcon />
          </ListItemIcon>
          <ListItemText primary="Applications" />
        </ListItem>
      </List>
    </>
  );
};

const LoadingPage = ({ searchValue }) => (
  <>
    <Typography variant="h1">Loading report: {searchValue}</Typography>
    <Skeleton variant="rounded" height={400} sx={{ marginBottom: "10px" }} />
    <Skeleton variant="rectangular" height={20} sx={{ marginBottom: "10px" }} />
    <Skeleton variant="rounded" height={130} sx={{ marginBottom: "10px" }} />
  </>
);

/**
 * timelineQuery
 * contains daily breakdown of how many time the searchValue matched fields
 * It does not contain any info on how many entites there are per day.
 */

/**
 * overviewQuery
 * contains unique distinct occurences of all query fields
 */

const SimpleSearchResultsView = (props) => {
  const {
    ready,
    timelineQuery,
    overviewQuery,
    findings,
    reload,
    pageHashParams,
    orgId,
  } = props;

  // Timeline at the top of the page
  const [timelineQueryControls, timelineQueryControlInterface] =
    useQueryControls("timelineQuery");
  const [timelineResultControls, timelineResultControlInterface] = // eslint-disable-line no-unused-vars
    useResultControls("timelineQuery");

  // Supporting hooks for Endpoint Overview
  const [overviewQueryControls, overviewQueryControlInterface] =
    useQueryControls("overviewQuery");
  const [overviewResultControls, overviewResultControlInterface] = // eslint-disable-line no-unused-vars
    useResultControls("overviewQuery");

  const [searchParams, setSearchParams] = useState({
    searchValue: pageHashParams.search,
    startTime: pageHashParams.start || defaultStartTime,
    endTime: pageHashParams.end || defaultEndTime,
  });

  const [autoSelectedFields, setAutoSelectedFields] = useState([]);
  const [useAutoSelect, setUseAutoSelect] = useState(true);

  // timeline Data
  const [lineData, setLineData] = useState([]);
  const [entityList, setEntityList] = useState({});

  // overview data
  const [overviewData, setOverviewData] = useState({
    originData: {},
    entityReports: {},
    entityEvents: {},
  });

  // auto select filters if the searchValue explicitly matches over 50% of the total fields
  const matchCountPercent = 0.5;

  useEffect(() => {
    if (ready) {
      fetchOverview(
        searchParams.searchValue,
        moment(Math.floor(searchParams.startTime)),
        moment(Math.ceil(searchParams.endTime))
      );
    }
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.searchValue !== pageHashParams["search"]) {
      const query = createTimelineQuery(
        new moment(),
        "year",
        pageHashParams.search,
        entityFields
      );

      Object.keys(timelineQueryControls).map((uuid) => {
        timelineQueryControlInterface.remove(uuid);
      });
      query.queryParams.map((qp) => timelineQueryControlInterface.add(qp));
      timelineResultControlInterface.update(
        "transform",
        query.resultParams.transform
      );
    }

    const newParams = {
      ...searchParams,
      searchValue: pageHashParams["search"],
    };

    if (pageHashParams["start"]) {
      newParams["startTime"] = pageHashParams.start;
    }
    if (pageHashParams["end"]) {
      newParams["endTime"] = pageHashParams.end;
    }

    setSearchParams(newParams);
  }, [pageHashParams]);

  useEffect(() => {
    const entityCount = {};

    entityTypes.forEach((eT) => {
      entityCount[eT.name] = {
        count: 0,
        disabled: false,
      };
    });

    const seriesIndexed = {};
    entityTypes.forEach((eT) => {
      seriesIndexed[eT.name] = {
        name: eT.name,
        data: [],
        color: eT.color,
      };
    });
    timelineQuery.forEach((o) => {
      const month = o.month < 10 ? `0${o.month}` : o.month;
      const day = o.day < 10 ? `0${o.day}` : o.day;
      const dateString = `${o.year}-${month}-${day}`;
      const uts = new moment(dateString).unix() * 1000;

      entityTypes.forEach((eT) => {
        const total = eT.queryFields.reduce((a, field) => {
          return a + o[`sum_${field.fieldName}`] || 0;
        }, 0);
        seriesIndexed[eT.name].data.push([uts, total]);
        entityCount[eT.name].count += total;
      });
    });

    Object.values(seriesIndexed).forEach((s) => {
      s.data.sort((a, b) => a[0] - b[0]);
    });
    setEntityList(entityCount);
    setLineData(Object.values(seriesIndexed));
  }, [timelineQuery]);

  useEffect(() => {
    const matchCount = Object.fromEntries(
      Object.keys(entityFields).map((k) => [k, 0])
    );
    overviewQuery.forEach((m) => {
      Object.keys(entityFields)
        .filter((k) => !!m[k])
        .forEach((k) => {
          if (m[k] == searchParams.searchValue) {
            matchCount[k] += 1;
          }
        });
    });
  }, [entityList]);

  useEffect(() => {
    if (overviewQuery.length > 0) {
      const _entityEvents = Object.fromEntries(
        entityTypes.map((eT) => [eT.name, new Set([])])
      );

      const matchCount = Object.fromEntries(
        Object.keys(entityFields).map((k) => [k, 0])
      );
      overviewQuery.forEach((m) => {
        Object.keys(entityFields)
          .filter((k) => !!m[k])
          .forEach((k) => {
            if (m[k] == searchParams.searchValue) {
              matchCount[k] += 1;
            }
          });
      });

      const sDev = Object.values(matchCount).sort().reverse();
      const matchCountSum = sDev.reduce((a, b) => a + b, 0);

      if (matchCountSum > 0) {
        setAutoSelectedFields(
          Object.keys(matchCount).filter(
            (k) => matchCount[k] >= matchCountSum * matchCountPercent
          )
        );
      }

      const tableColumns = Object.keys(entityFields).map((k) => {
        return { title: k, field: k, searchable: false };
      });
      const _uniqueValues = {};
      tableColumns.forEach((column) => {
        const _uniques = {};
        overviewQuery
          .filter((o) => o[column.field])
          .forEach((o) => {
            const lK = o[column.field].toLowerCase
              ? o[column.field].toLowerCase()
              : o[column.field];
            if (_uniques[lK] === undefined) {
              _uniques[lK] = {
                value: o[column.field],
                count: 0,
                origin: [],
                sparkline: {},
              };
            }
            _uniques[lK].count += 1;
            _uniques[lK].origin.push(o);
            // XXX TODO: the sum of all sparkline values does not match the sum of all sum_totals
            if (o.sparkline) {
              o.sparkline.forEach((v) => {
                if (_uniques[lK].sparkline[v.start] === undefined) {
                  _uniques[lK].sparkline[v.start] = 0;
                }
                _uniques[lK].sparkline[v.start] += v.value;
              });
            }
            // loop over entities and build spark data based off matching rows
            entityTypes.forEach((eT) => {
              if (
                eT.matchFields.map((f) => f.fieldName).includes(column.field)
              ) {
                _entityEvents[eT.name].add(o);
              }
            });
          });
        if (Object.keys(_uniques).length > 0) {
          _uniqueValues[column.field] = _uniques;
        }
      });

      const _attributeMatchCount = {};
      const lowerSearch = searchParams.searchValue.toLowerCase();

      const matchDistanceRange = [99999, 0];
      Object.keys(_uniqueValues).forEach((k) => {
        const uMatches = Object.keys(_uniqueValues[k]).filter((v) =>
          v.includes(lowerSearch)
        );
        const lens = uMatches.map((v) => v.length - lowerSearch.length);
        if (lens.length > 0) {
          lens.sort((a, b) => a - b);
          const minMatchDistance = lens[0];
          const avgMatchDistance =
            lens.reduce((a, b) => a + b, 0) / lens.length;
          const maxMatchDistance = lens.lastItem;
          const totalValues = Object.keys(_uniqueValues[k]).length;

          if (minMatchDistance < matchDistanceRange[0]) {
            matchDistanceRange[0] = minMatchDistance;
          }
          if (maxMatchDistance > matchDistanceRange[1]) {
            matchDistanceRange[1] = maxMatchDistance;
          }
          _attributeMatchCount[k] = {
            matches: uMatches,
            totalValues,
            dSignal:
              (minMatchDistance + maxMatchDistance) / 2 / maxMatchDistance,
            mSignal: uMatches.length / totalValues,
            minMatchDistance,
            avgMatchDistance,
            maxMatchDistance,
          };
        }
      });

      const _entityReports = {};
      Object.keys(_attributeMatchCount).forEach((k) => {
        _attributeMatchCount[k].health = normalize(
          _attributeMatchCount[k].avgMatchDistance,
          ...matchDistanceRange
        );
        const entMatches = entityTypes.filter((eT) =>
          eT.matchFields.includes(entityFields[k])
        );
        entMatches.forEach((e) => {
          if (_entityReports[e.name] === undefined) {
            _entityReports[e.name] = {};
          }
          _entityReports[e.name][k] = _attributeMatchCount[k];
        });
      });

      console.log(
        "NOT SURE IF WE NEED entityEvents ANYMORE.  ONLY LEGACY OVERVIEW NEEDS IT"
      );
      console.log("=================================");
      console.log(_uniqueValues);
      console.log(_entityReports);
      console.log("---------------------------------");
      console.log(_entityEvents);
      console.log("=================================");

      setOverviewData({
        originData: _uniqueValues,
        entityEvents: _entityEvents,
        entityReports: _entityReports,
      });
    }
  }, [overviewQuery]);

  const fetchOverview = (searchValue, startTime, endTime) => {
    const entityNames = Object.keys(entityList).filter(
      (eT) => !entityList[eT].disabled
    );

    const query = createOverviewEndpointQuery(
      startTime,
      endTime,
      searchValue,
      getQueryFieldsByEntityName(entityNames),
      Object.values(entityFields)
    );

    Object.keys(overviewQueryControls).map((uuid) => {
      overviewQueryControlInterface.remove(uuid);
    });
    query.queryParams.map((qp) => overviewQueryControlInterface.add(qp));
    overviewResultControlInterface.update(
      "transform",
      query.resultParams.transform
    );
    reload();
  };

  const handleBrushTimes = (xaxis) => {
    setPageHashParams({
      ...pageHashParams,
      start: Math.floor(xaxis.min),
      end: Math.ceil(xaxis.max),
    });
  };

  const debounceSetAxis = debounce((xaxis) => {
    handleBrushTimes(xaxis);
  }, 2000);

  const handleBrushScroll = (
    chartContext,
    { xaxis, yaxis },
    shouldDebounce = true
  ) => {
    if (shouldDebounce) {
      debounceSetAxis(xaxis);
    } else {
      handleBrushTimes(xaxis);
    }
  };

  const handleLegendClick = (chartContext, seriesIndex, config) => {
    const { series } = config.config;
    const selectedSeriesItemName = series[seriesIndex].name;

    const enabled = [];
    series.forEach((seriesItem) => {
      const isClicked = seriesItem.name !== selectedSeriesItemName;
      if (isClicked) {
        if (seriesItem.data.length > 0) {
          enabled.push(seriesItem.name);
        }
      } else if (seriesItem.data.length === 0) {
        enabled.push(seriesItem.name);
      }
    });

    // copy over the items so we're not mutating anything. #sanity
    const newEntityList = {};
    Object.keys(entityList).forEach((eT) => {
      newEntityList[eT] = { ...entityList[eT] };
      if (enabled.includes(eT)) {
        newEntityList[eT].disabled = false;
      } else {
        newEntityList[eT].disabled = true;
      }
    });
    setEntityList(newEntityList);
  };

  if (!searchParams.searchValue) {
    return <SimpleSearchInputPage />;
  }
  if (ready === false) {
    return <LoadingPage searchValue={searchParams.searchValue} />;
  }
  return (
    <Grid container spacing={2} alignItems="stretch">
      <Grid item xs={12}>
        <Alert severity="info">
          This feature is currently in an early alpha development stage. While
          usable, being pre-release software means it may contain bugs and
          stability issues. Please report any encountered problems so we can
          improve the software quality before official release. Thank you for
          understanding!
        </Alert>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h1">{searchParams.searchValue}</Typography>
      </Grid>
      <Grid item xs={12}>
        <Paper>
          {lineData.length > 0 && (
            <SSLine
              data={lineData}
              entityList={entityList}
              findings={findings}
              extra={Object.fromEntries(
                Object.keys(entityList).map((entity) => [
                  entity,
                  abbreviateNumber(entityList[entity].count),
                ])
              )}
              brushScrolled={handleBrushScroll}
              legendClick={handleLegendClick}
              min={searchParams.startTime}
              max={searchParams.endTime}
            />
          )}
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Typography align="center" variant="h6">
          Date Range:{" "}
          {moment(Math.floor(searchParams.startTime)).format(
            "MMM D, YYYY h:mm A"
          )}{" "}
          -{" "}
          {moment(Math.ceil(searchParams.endTime)).format("MMM D, YYYY h:mm A")}
        </Typography>
        {useAutoSelect && autoSelectedFields.length > 0 && (
          <Alert
            severity="info"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => setUseAutoSelect(false)}
              >
                Clear Filters
              </Button>
            }
          >
            Filters for {autoSelectedFields} were automatically added based on
            your search for "{searchParams.searchValue}" in order to focus the
            results on the most relevant matches. To see all results, you can
            clear these filters.
          </Alert>
        )}
      </Grid>
      <Grid item xs={12}>
        <>
          {entityTypes
            .filter(
              (eT) => entityList[eT.name] && !entityList[eT.name].disabled
            )
            .filter((eT) => overviewData.entityEvents[eT.name])
            .map((eT) => (
              <LegacyOverview
                key={eT.name}
                orgId={orgId}
                entity={eT}
                entityEvents={overviewData.entityEvents[eT.name]}
                originData={overviewData.originData}
                searchValue={searchParams.searchValue}
                autoSelectedFields={useAutoSelect ? autoSelectedFields : []}
              ></LegacyOverview>
            ))}
        </>
      </Grid>
    </Grid>
  );
};

const SimpleSearchPageView = (props) => {
  if (props.pageHashParams.search) {
    return <SimpleSearchResultsView {...props} />;
  } else {
    return <SimpleSearchInputPage {...props} />;
  }
};

SimpleSearchPageView.propTypes = {
  ready: PropTypes.bool,
  orgId: PropTypes.string.isRequired,
  timelineQuery: PropTypes.arrayOf(PropTypes.shape({})),
  overviewQuery: PropTypes.arrayOf(PropTypes.shape({})),
  findings: PropTypes.arrayOf(PropTypes.shape({})),
};

SimpleSearchPageView.defaultProps = {
  timelineQuery: [],
  overviewQuery: [],
  findings: [],
};

const mapStateToProps = (state) => {
  return {
    orgId: state.location.payload.orgId,
  };
};

const mapDispatchToProps = (dispatch, { orgId }) => ({
  reload: (force) => dispatch(loadPageData(force)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SimpleSearchPageView);
